import { useCallback, useMemo } from 'react';
import { useFeature } from '@optimizely/react-sdk';

import { InstanceOperationsName, OperationsName } from '@frontend/types';

import { useOpalUser } from './useOpalUser';

export function useOperations() {
  const { opalUser } = useOpalUser();
  const [isFbpReleased] = useFeature('fbp_released');

  const userOperations = useMemo(
    () => opalUser?.instance?.operations,
    [opalUser?.instance?.operations]
  );

  const instanceOperations = useMemo(
    () => opalUser?.instance?.product_capabilities,
    [opalUser?.instance?.product_capabilities]
  );

  /**
   * Function to check if user has at least one of the required operations.
   * @param operations - list of operations of which at least one is required (checked using Logical OR)
   * @returns boolean indicating if user has at least one of the required operations
   */
  const canUser = useCallback(
    (operations: OperationsName[]) => {
      return operations.some(operation => userOperations?.find(p => p.name === operation)?.allowed);
    },
    [userOperations]
  );

  /**
   * Function to check if instance has at least one of the required capabilities enabled.
   *
   * @param capabilities - list of capabilities of which at least one is required (checked using Logical OR)
   * @param fallback - return value when FBP is not released (default: true)
   *                   Set to false when combining with existing feature flags.
   * @returns boolean indicating if instance has at least one of the required capabilities enabled
   *
   * Note: The fallback parameter is temporary during FBP migration.
   * TODO: Remove fallback parameter after FBP release is complete
   */
  const canInstance = useCallback(
    (capabilities: InstanceOperationsName[], fallback: boolean = true) => {
      // If FBP not released yet, return fallback value
      if (!isFbpReleased) {
        return fallback;
      }

      // FBP is released, do actual capability check
      return capabilities.some(
        capability => instanceOperations?.find(p => p.name === capability)?.allowed
      );
    },
    [isFbpReleased, instanceOperations]
  );

  return { operations: userOperations, instanceOperations, canUser, canInstance };
}
